import { useMemo } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { EditJobPosting, type EditableJob } from '../../components/employer/EditJobPosting';
import { useEmployerJobs } from '../../hooks/useEmployerJobs';

export function JobEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId } = useParams<{ jobId: string }>();
  const { jobId: jobIdFromState } = (location.state as { jobId?: number } | null) ?? {};

  const jobsQuery = useEmployerJobs({ status: 'all' });
  const jobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);

  const job = useMemo(() => {
    const candidateIds: string[] = [];

    if (typeof jobIdFromState === 'number' && !Number.isNaN(jobIdFromState)) {
      candidateIds.push(String(jobIdFromState));
    }

    if (typeof jobId === 'string') {
      candidateIds.push(jobId);
    }

    return jobs.find((entry) => candidateIds.includes(String(entry.id)));
  }, [jobs, jobId, jobIdFromState]);

  if (jobsQuery.isPending) {
    return (
      <main className="min-h-screen bg-[#080a1a] pb-24" aria-label="Loading job editor">
        <div className="mx-auto flex h-full max-w-3xl flex-col items-center justify-center gap-4 px-4 py-16 text-center sm:px-6 lg:px-8">
          <h1 className="text-2xl font-semibold text-white">Loading job...</h1>
          <p className="text-sm text-gray-400">Fetching the latest details for this posting.</p>
        </div>
      </main>
    );
  }

  if (!job) {
    return (
      <main className="min-h-screen bg-[#080a1a] pb-24" aria-labelledby="job-not-found-title">
        <div className="mx-auto flex h-full max-w-3xl flex-col items-center justify-center gap-4 px-4 py-16 text-center sm:px-6 lg:px-8">
          <h1 id="job-not-found-title" className="text-3xl font-semibold text-white">Job not found</h1>
          <p className="text-sm text-gray-400">The position may have been deleted or the link is incorrect.</p>
          <Button asChild className="bg-gold-gradient text-black hover:brightness-110">
            <Link to="/employer/dashboard">Return to dashboard</Link>
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#070917] pb-24" aria-labelledby="job-editor-title">
      <header className="border-b border-white/10 bg-[#0d1022]/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.35em] text-gray-500">Editing</p>
              <h1 id="job-editor-title" className="text-3xl font-semibold text-white sm:text-4xl">{job.title}</h1>
              <p className="text-sm text-gray-400">Update the role details, responsibilities, and visibility.</p>
            </div>
            <div className="flex flex-col items-end gap-2 text-sm text-gray-300">
              <span>Your organisation</span>
              <Badge className="w-fit bg-white/10 text-gray-200">Job ID {job.id}</Badge>
            </div>
          </div>
          <Button variant="outline" className="w-fit border-white/20 text-white hover:bg-white/10" onClick={() => navigate(-1)}>
            Back to dashboard
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <Card className="border-white/10 bg-white/[0.03]">
          <CardHeader>
            <CardTitle className="text-lg text-white">Job details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            <EditJobPosting
              job={
                {
                  id: job.id,
                  title: job.title,
                  location: job.location,
                  salary: job.salary,
                  type: job.type,
                  posted: job.posted,
                  applicants: job.applicants,
                  views: job.views,
                  status: job.status,
                  description: job.description,
                  requirements: (job as { requirements?: string[] }).requirements ?? [],
                  benefits: (job as { benefits?: string[] }).benefits ?? [],
                  returnerFriendly: (job as { returnerFriendly?: boolean }).returnerFriendly ?? false,
                  skillTestId:
                    (job as { skillTestId?: string | number }).skillTestId != null
                      ? String((job as { skillTestId?: string | number }).skillTestId)
                      : undefined,
                } as EditableJob
              }
              successMessage="Draft updated. Submit changes to publish."
              deleteMessage="Job removed."
              onClose={() => navigate(-1)}
              onSave={() => {
                navigate(-1);
              }}
              onDelete={() => {
                navigate('/employer/dashboard');
              }}
            />
          </CardContent>
        </Card>
      </main>
    </main>
  );
}
